import { useEffect, useState } from "react";

import { pricingService, type PriceCatalogRow } from "../api/pricingService";
import { EMPTY_PRICE_BOOK, priceBookFrom, type PriceBook } from "./priceBook";

/**
 * 价目表(整表一次,进程内缓存)。卡墙 / 工作台的积分标从它取数。
 *
 * ★ 模块级缓存 + 共享 in-flight promise:卡墙上有几十张卡,每张都挂一次 effect 的话
 *   首屏会打出几十个一模一样的请求。
 * ★ 取失败**不兜数字**:返回 `EMPTY_PRICE_BOOK`(`ready === false`),由卡面渲染骨架。
 *   给兜底数字等于把一个可能过期的价钱说成真的 —— 用户会信,而这正是这一批在修的病。
 */
let cached: PriceBook | null = null;
let inflight: Promise<PriceCatalogRow[]> | null = null;

/** 测试用:清掉进程内缓存(否则用例之间会互相看到对方的桩)。 */
export function __resetPriceBookCache() {
  cached = null;
  inflight = null;
}

/**
 * 测试用:**同步**塞一份价目表进缓存。
 *
 * ★ 为什么要有它:真实里价目表是一跳异步,首帧卡面是骨架。组件测如果也走那一跳,
 *   几十个既有用例都得改成 `await waitFor` —— 而它们要测的根本不是价钱。
 *   预置缓存让 `usePriceBook()` 首帧就有值,既有的同步断言原样成立。
 * ★ 「首帧没价该怎么显示」这条不变量**另有专测**(直接喂 `EMPTY_PRICE_BOOK`),
 *   不依赖这个预置 —— 所以预置不会把那条路径遮掉。
 */
export function __seedPriceBookCache(rows: PriceCatalogRow[]) {
  cached = priceBookFrom(rows);
  inflight = Promise.resolve(rows);
}

export function usePriceBook(): { book: PriceBook; loading: boolean } {
  const [book, setBook] = useState<PriceBook>(cached ?? EMPTY_PRICE_BOOK);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (cached) {
      return;
    }
    let alive = true;
    inflight = inflight ?? pricingService.catalog();
    inflight
      .then((rows) => {
        cached = priceBookFrom(rows ?? []);
        if (alive) setBook(cached);
      })
      .catch(() => {
        // 不缓存失败结果:下次挂载还要再试一次。也不兜数字。
        inflight = null;
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return { book, loading };
}
